import {
    AfterContentInit,
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    computed,
    contentChildren,
    effect,
    inject,
    input,
    InputSignal,
    OnDestroy,
    OnInit,
    Signal,
    signal,
    WritableSignal
} from '@angular/core';
import {ActionItemComponent} from '../action-item/action-item.component';
import {AppQuery} from '../../queries/app.query';
import {Observable} from 'rxjs/internal/Observable';
import {AppService} from '../../services/app/app.service';
import {combineLatest, firstValueFrom, Subscription} from 'rxjs';
import {map, take} from 'rxjs/operators';
import {TextItem} from '../../api/models/text-item';
import {VaultQuery} from '../../queries/vault.query';
import {LOCAL_FILE_SERVICE, LocalFileService} from '../../services/local-file/local-file.service';
import {SearchQuery} from '../../queries/search.query';
import {DocumentQuery} from '../../queries/document.query';
import {HapticsService} from '../../services/haptics/haptics.service';
import {PanelLocationEnum} from '../../enums/panel-location.enum';
import {AnnotationService} from '../../services/annotation/annotation.service';
import {SelectedItemDescription} from '../../models/selected-item/selected-item-description.model';
import {AnnotationQuery} from '../../queries/annotation.query';
import {DialogService} from '../../services/dialog/dialog.service';
import {SnackbarService} from '../../services/snackbar/snackbar.service';
import {TaskDocumentService} from '../../services/task-document/task-document.service';
import {IconsComponent} from '../dummy-components/icons.component';
import {SearchService} from '../../services/search/search.service';
import {AnnotationCollectionType} from '../../types/annotation/annotation-collection.type';
import {AmagnoLinkService} from '../../services/amagno-link/amagno-link.service';
import {environment} from '../../../environments/environment';
import {AsyncPipe, NgTemplateOutlet} from '@angular/common';
import {TranslateModule} from '@ngx-translate/core';
import {ExtAsyncPipe} from '../../pipes/ext-async.pipe';
import {SystemInfoService} from '../../services/system-info/system-info.service';
import {TooltipPositionEnum} from '../../enums/tooltip/tooltip-position.enum';
import {ButtonComponent} from '../buttons/button/button.component';
import {AppButtonTypeEnum} from '../../enums/button/app-button-type.enum';
import {ButtonColorEnum} from '../../enums/button/button-color.enum';
import {Vault} from '../../api/models/vault';
import {StampService} from '../../services/stamp/stamp.service';
import {UrlParamsService} from '../../services/url-params/url-params.service';
import {DocumentViewService} from '../../services/document/document-view/document-view.service';
import {DocumentViewModeEnum} from '../../enums/document-view-mode.enum';
import {PanelComponent} from '../panel/panel.component';
import {PanelService} from '../../services/panel/panel.service';
import {NavigationStart, Router} from '@angular/router';

@Component({
    host: { '[class.hide]': 'this.hideActionButton()' },
    selector: 'app-floating-action-button',
    templateUrl: './floating-action-button.component.html',
    styleUrls: ['./floating-action-button.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        TranslateModule,
        ExtAsyncPipe,
        AsyncPipe,
        ButtonComponent,
        NgTemplateOutlet,
        PanelComponent,
    ]
})
export class FloatingActionButtonComponent extends IconsComponent implements OnInit, AfterContentInit, AfterViewInit, OnDestroy {
    public location: InputSignal<PanelLocationEnum> = input<PanelLocationEnum>(PanelLocationEnum.Sidebar);
    public contentActionItems: Signal<ReadonlyArray<ActionItemComponent>> = contentChildren(ActionItemComponent, { descendants: true });
    protected hideActionButton: WritableSignal<boolean>;
    protected isLargeMenuOpen: WritableSignal<boolean>;
    protected sortedActionItems: WritableSignal<Array<ActionItemComponent> | undefined>;
    protected textItems$: Observable<Array<TextItem>>;
    protected isShowingSmallMenu$: Observable<boolean>;
    protected documentViewMode$: Observable<DocumentViewModeEnum>;
    protected countTemporaryAnnotations$: Observable<number>;
    protected selectedAnnotation$: Observable<SelectedItemDescription | undefined>;
    protected isSelectedAnnotationTemporary$: Observable<boolean>;
    protected canShowNextButton: Signal<boolean>;
    protected panelId: string;
    protected isAddingStamp: Signal<boolean>;
    protected PanelLocationEnum = PanelLocationEnum;
    protected readonly AppButtonTypeEnum = AppButtonTypeEnum;
    protected readonly TooltipPositionEnum = TooltipPositionEnum;
    protected readonly ButtonColorEnum = ButtonColorEnum;
    protected readonly DocumentViewModeEnum = DocumentViewModeEnum;
    private readonly subscription: Subscription;
    private readonly amagnoLinkService = inject(AmagnoLinkService);
    private readonly annotationQuery = inject(AnnotationQuery);
    private readonly annotationService = inject(AnnotationService);
    private readonly appQuery = inject(AppQuery);
    private readonly appService = inject(AppService);
    private readonly dialogService = inject(DialogService);
    private readonly snackbarService = inject(SnackbarService);
    private readonly documentQuery = inject(DocumentQuery);
    private readonly documentViewService = inject(DocumentViewService);
    private readonly hapticsService = inject(HapticsService);
    private readonly localFileService = inject<LocalFileService>(LOCAL_FILE_SERVICE);
    private readonly panelService = inject(PanelService);
    private readonly router = inject(Router);
    private readonly searchQuery = inject(SearchQuery);
    private readonly searchService = inject(SearchService);
    private readonly stampService = inject(StampService);
    private readonly systemInfoService = inject(SystemInfoService);
    private readonly taskDocumentService = inject(TaskDocumentService);
    private readonly urlParamsService = inject(UrlParamsService);
    private readonly vaultQuery = inject(VaultQuery);

    public constructor() {
        super();
        this.subscription = new Subscription();
        this.isLargeMenuOpen = signal(false);
        this.hideActionButton = signal(false);
        this.sortedActionItems = signal(undefined);
        this.isShowingSmallMenu$ =
            combineLatest([
                this.appQuery.isShowingShortMenuSidebar$,
                this.appQuery.isShowingShortMenuContent$
            ])
                .pipe(map(([menuSidebar, menuContent]: [boolean, boolean]) => {
                    if (this.location() === PanelLocationEnum.Sidebar) {
                        return menuSidebar;
                    } else {
                        return menuContent;
                    }
                }));
        this.selectedAnnotation$ = this.annotationQuery.selectedAnnotation$;
        this.isSelectedAnnotationTemporary$ = this.selectedAnnotation$.pipe(map(selectedItemDescription => {
            if (selectedItemDescription?.id) {
                return selectedItemDescription.id.includes('TEMP_');
            }
            return false;
        }));
        this.textItems$ = this.searchQuery.listSelectedTextItems$;
        this.appService.setShowingSmallMenu(PanelLocationEnum.Sidebar, false);
        this.documentViewMode$ = this.documentQuery.documentViewMode$;
        this.countTemporaryAnnotations$ = this.annotationQuery.temporaryAnnotations$.pipe(map(temporaryAnnotations => {
            let num = 0;
            if (temporaryAnnotations) {
                const annotations = temporaryAnnotations as unknown as { [key: string]: AnnotationCollectionType };
                Object.keys(annotations)
                    .forEach(key => {
                        num += annotations[key].length;
                    });
            }
            return num;
        }));

        this.isAddingStamp = computed(() => this.documentViewService.getCurrentStampSignal()() !== undefined);

        this.canShowNextButton = computed(() => {
            const currentStamp = this.documentViewService.getCurrentStampSignal()();
            const isTaskPage = !!this.urlParamsService.getUrlParamsSignal()().taskId;
            const hasMoreStampableDocumentsInList = this.taskDocumentService.getHasMoreDocumentsInTaskListSignal()();

            return isTaskPage && hasMoreStampableDocumentsInList && !!currentStamp;
        });
        this.panelId = '';

        effect(() => {
            const isPanelOpen = this.panelService.isPanelOpen(this.location());
            this.hideActionButton.set(isPanelOpen ? false : this.sortedActionItems()?.length === 0);
        });

        effect(() => {
            if (this.panelService.isPanelOpen(this.location())) {
                this.isLargeMenuOpen.set(false);
            }
        });

        effect(() => {
            this.updateContentElements();
        });

        effect(onCleanup => {
            const subscriptions = this.contentActionItems()
                .map(item => item.clicked.subscribe(() => this.isLargeMenuOpen.set(false)));
            onCleanup(() => subscriptions.forEach(subscription => subscription.unsubscribe()));
        });
    }

    public ngOnInit(): void {
        this.subscription.add(this.isShowingSmallMenu$.subscribe(isShowingShortMenu => {
            if (isShowingShortMenu) {
                this.isLargeMenuOpen.set(false);
            }
        }));

        // Close all open menus when a navigation starts
        this.subscription.add(this.router.events.subscribe(event => {
            if (event instanceof NavigationStart) {
                this.isLargeMenuOpen.set(false);
                this.appService.setShowingSmallMenu(this.location(), false);
            }
        }));
    }

    public ngAfterContentInit(): void {
        this.panelId =
            'ACTION_MENU' + (window.location.pathname.replace(/\/(\w{8}-?)(\w{4}-?){3}(\w{12}-?)/gm, '')
                .replace(/\//gm, '_') + '_' + this.location())
                .toUpperCase();
    }

    public ngAfterViewInit(): void {
        this.appService.setHasActionButton(true);
    }

    public ngOnDestroy(): void {
        this.panelService.closePanel(this.location());
        this.subscription.unsubscribe();
        this.appService.setShowingSmallMenu(this.location(), false);
        this.appService.setHasActionButton(false);
    }

    public async closeMenu(): Promise<void> {
        const documentViewMode = await firstValueFrom(this.documentViewMode$);
        if (documentViewMode !== DocumentViewModeEnum.Annotations && documentViewMode !== DocumentViewModeEnum.Drawing) {
            this.appService.setShowingSmallMenu(this.location(), false);
            this.isLargeMenuOpen.set(false);
        } else {
            this.closeAction();
        }
    }

    protected async openSmallMenu(): Promise<void> {
        await this.hapticsService.hapticsMedium();
        this.appService.setShowingSmallMenu(this.location(), true);
        this.isLargeMenuOpen.set(false);
    }

    protected async openLargeMenu(): Promise<void> {
        await this.hapticsService.hapticsHeavy();
        this.isLargeMenuOpen.set(true);
    }

    protected activateActionItem(actionItem: ActionItemComponent): void {
        this.appService.setShowingSmallMenu(this.location(), false);
        actionItem.activate();
    }

    protected async searchForTextGlobal(): Promise<void> {
        const searchWord = await this.getSearchWord();
        if (searchWord.length === 0) {
            return;
        }

        await this.executeSearch(searchWord);
    }

    protected async searchForTextLocal(): Promise<void> {
        this.appService.setIsNotShowingSearchOnGoingBack(true);

        const searchWord = await this.getSearchWord();
        if (searchWord.length === 0) {
            return;
        }

        const currentVault = this.vaultQuery.getFirstActiveVault();
        if (!currentVault) {
            return;
        }

        await this.executeSearch(searchWord, currentVault);
    }

    protected async shareSearchText(): Promise<void> {
        this.appService.setIsNotShowingSearchOnGoingBack(true);
        const searchWord = await this.getSearchWord();
        if (searchWord.length === 0) {
            return;
        }
        try {
            await this.localFileService.shareUrl(searchWord);
            this.snackbarService.showSuccess('COPIED_TEXT_TO_CLIPBOARD', {
                text: searchWord
            });
        } catch (error) {
            if (this.systemInfoService.isCapacitor()) {
                this.snackbarService.showError('ERROR_SHARING', error as Error);
            } else {
                this.snackbarService.showError('ERROR_COPY_TO_CLIPBOARD', error as Error);
            }
        }
    }

    protected saveAction(): void {
        this.annotationService.actionMenuOnSave.emit();
    }

    protected async deleteAction(): Promise<void> {
        if (this.annotationService.actionMenuOnDelete !== undefined) {
            await this.annotationService.actionMenuOnDelete();
        }
    }

    protected async showAnnotationMenuAction(): Promise<void> {
        const documentId = this.urlParamsService.getUrlParamsSignal()().documentId;
        if (!documentId) {
            return;
        }

        await this.annotationService.openAnnotationPanel(documentId);
    }

    protected copyAction(): void {
        if (this.annotationService.actionMenuOnCopy !== undefined) {
            this.annotationService.actionMenuOnCopy();
        }
    }

    protected nextAction(): void {
        this.annotationService.actionMenuOnNext.next();
    }

    protected saveStampAction(): void {
        this.saveAction();
        const sub = this.annotationService.actionMenuOnSaveDone.subscribe(() => {
            this.stampService.openNextStampDocument.next();
            sub.unsubscribe();
        });
    }

    protected async cancelStampingAction(): Promise<void> {
        const cancelStamping = await this.dialogService.showCancelStampingDialog();
        if (cancelStamping) {
            this.stampService.openNextStampDocument.next();
        }
    }

    protected closeAction(): void {
        this.annotationService.actionMenuOnClose.emit();
    }

    protected async closeMenuFromMenu(): Promise<void> {
        const documentViewMode = await firstValueFrom(this.documentViewMode$);
        if (documentViewMode === DocumentViewModeEnum.Viewing || documentViewMode === DocumentViewModeEnum.ClickFill) {
            await this.closeMenu();
        }
    }

    private updateContentElements(): void {
        this.sortedActionItems.set([...this.contentActionItems()].sort((a, b) => {
            return a.smallMenuOrderNumber() > b.smallMenuOrderNumber() ? 1 : -1;
        }));
    }

    private async getSearchWord(): Promise<string> {
        return await firstValueFrom(this.textItems$.pipe(
            take(1),
            map(items =>
                items.map(item => item.text)
                    .join(' ')
            )));
    }

    private async executeSearch(searchWord: string, vaultId?: Vault): Promise<void> {
        if (searchWord.startsWith(environment.deepLinkPrefix) || searchWord.startsWith('url:' + environment.deepLinkPrefix)) {
            await this.amagnoLinkService.openAmagnoLinkByAmagnoDeepLink(searchWord);
        } else {
            await this.searchService.openSearch(searchWord, vaultId);
        }
    }
}
